// Chamadas da conta, do pagamento, do monitoramento e do lote. O cookie de sessão é
// httpOnly: o navegador manda sozinho, e o código daqui nunca vê o token.

import type {
  ApiKeyView,
  BillingOverview,
  InvoiceView,
  LoteResposta,
  MonitorItem,
  MonitorOverview,
  PlatformInfo,
  SessionInfo,
  SessionPayload,
  SubscriptionView,
} from "../../shared/conta";
import type { BillingCycle, PlanSlug } from "../../shared/plans";
import { IS_STATIC_BUILD } from "./api";

export class ContaError extends Error {
  constructor(message: string, public status: number, public code?: string, public extra?: Record<string, unknown>) {
    super(message);
  }
}

async function call<T>(url: string, init: { method?: string; json?: unknown; signal?: AbortSignal } = {}): Promise<T> {
  if (IS_STATIC_BUILD) {
    throw new ContaError("A conta precisa do servidor do Teia. Esta é a versão estática, só de consulta.", 503, "sem_servidor");
  }
  let response: Response;
  try {
    response = await fetch(url, {
      method: init.method ?? (init.json === undefined ? "GET" : "POST"),
      headers: init.json === undefined ? undefined : { "Content-Type": "application/json" },
      body: init.json === undefined ? undefined : JSON.stringify(init.json),
      credentials: "same-origin",
      signal: init.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    throw new ContaError("Sem conexão com o servidor. Confira a internet e tente de novo.", 0, "rede");
  }
  if (response.status === 204) return undefined as T;
  const payload = (await response.json().catch(() => ({}))) as { message?: string; code?: string } & Record<string, unknown>;
  if (!response.ok) {
    const { message, code, ...extra } = payload;
    throw new ContaError(message ?? `Erro ${response.status}`, response.status, code, extra);
  }
  return payload as T;
}

// -- sessão --------------------------------------------------------------------

export const apiSession = (signal?: AbortSignal) => call<SessionPayload>("/api/auth/sessao", { signal });

export const apiLogin = (email: string, senha: string) =>
  call<SessionPayload>("/api/auth/entrar", { json: { email, senha } });

export const apiSignup = (input: { nome: string; email: string; senha: string; aceite: boolean }) =>
  call<SessionPayload>("/api/auth/criar-conta", { json: input });

export const apiLogout = () => call<void>("/api/auth/sair", { method: "POST", json: {} });

export const apiForgot = (email: string) => call<{ message: string }>("/api/auth/recuperar", { json: { email } });

export const apiReset = (token: string, senha: string) =>
  call<SessionPayload>("/api/auth/redefinir", { json: { token, senha } });

export const apiPlatform = (signal?: AbortSignal) => call<PlatformInfo>("/api/plataforma", { signal });

// -- conta ---------------------------------------------------------------------

export const apiUpdateProfile = (nome: string) => call<SessionPayload>("/api/conta/perfil", { method: "PATCH", json: { nome } });

export const apiChangePassword = (atual: string, nova: string) =>
  call<{ message: string }>("/api/conta/senha", { json: { atual, nova } });

export const apiSessions = () => call<{ itens: SessionInfo[] }>("/api/conta/sessoes");

export const apiRevokeSession = (id: string) => call<void>(`/api/conta/sessoes/${encodeURIComponent(id)}`, { method: "DELETE" });

export const apiRevokeOtherSessions = () => call<{ encerradas: number }>("/api/conta/sessoes/encerrar-outras", { json: {} });

export const apiDeleteAccount = (senha: string) => call<void>("/api/conta", { method: "DELETE", json: { senha } });

// -- chaves de API -------------------------------------------------------------

export const apiKeys = () => call<{ itens: ApiKeyView[]; limite: number }>("/api/conta/chaves");

export const apiCreateKey = (nome: string) => call<{ chave: string; item: ApiKeyView }>("/api/conta/chaves", { json: { nome } });

export const apiRevokeKey = (id: string) => call<void>(`/api/conta/chaves/${encodeURIComponent(id)}`, { method: "DELETE" });

// -- monitoramento -------------------------------------------------------------

export const apiMonitor = () => call<MonitorOverview>("/api/conta/monitor");

export const apiMonitorStatus = (cnpj: string, signal?: AbortSignal) =>
  call<{ monitorado: boolean; item: MonitorItem | null }>(`/api/conta/monitor/${encodeURIComponent(cnpj.replace(/\D/g, ""))}`, { signal });

export const apiMonitorAdd =(cnpj: string) => call<{ item: MonitorItem }>("/api/conta/monitor", { json: { cnpj } });

export const apiMonitorRemove = (cnpj: string) =>
  call<void>(`/api/conta/monitor/${encodeURIComponent(cnpj.replace(/\D/g, ""))}`, { method: "DELETE" });

// -- pagamento -----------------------------------------------------------------

export const apiBilling = () => call<BillingOverview>("/api/pagamentos/assinatura");

export const apiCheckout = (input: { plano: PlanSlug; ciclo: BillingCycle; documento?: string }) =>
  call<{ checkoutUrl: string | null; assinatura: SubscriptionView }>("/api/pagamentos/assinar", { json: input });

export const apiChangePlan = (input: { plano: PlanSlug; ciclo: BillingCycle }) =>
  call<{ efeito: "imediato" | "no_fim_do_ciclo" | "mantido"; extraChargeUrl: string | null; assinatura: SubscriptionView }>(
    "/api/pagamentos/mudar-plano",
    { json: input },
  );

export const apiCancel = (motivo?: string) =>
  call<{ efeito: "agendado" | "imediato"; assinatura: SubscriptionView }>("/api/pagamentos/cancelar", { json: { motivo } });

export const apiResume = () => call<{ assinatura: SubscriptionView }>("/api/pagamentos/reativar", { json: {} });

export const apiInvoices = (atualizar = false) =>
  call<{ itens: InvoiceView[]; atualizadoNoProvedor: boolean }>(`/api/pagamentos/faturas${atualizar ? "?atualizar=1" : ""}`);

export const apiPortal = () => call<{ url: string }>("/api/pagamentos/portal", { json: {} });

// -- lote ----------------------------------------------------------------------

export const apiBatch = (cnpjs: string[], signal?: AbortSignal) => call<LoteResposta>("/api/lote", { json: { cnpjs }, signal });

export const apiBatchUsage = () => call<{ usadoHoje: number; limiteDia: number }>("/api/lote/uso");
